/**
 * TARS donut chart (amCharts 5 PieChart + PieSeries).
 * Same table JSON as bar/column charts — `rowsToTARSBarSeries` from `tarsBarChartHelpers.ts`.
 * Labels outside slices with connector ticks; no legend; optional `title` + per-slice colors.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-ignore amCharts5 bundled IIFE registers globals
import "../../../libs/amcharts5/index";
// @ts-ignore percent charts (PieChart, PieSeries)
import "../../../libs/amcharts5/percent";
// @ts-ignore Animated theme
import "../../../libs/amcharts5/themes/Animated";

import type { MutableRefObject } from "react";
import { useLayoutEffect, useMemo, useRef } from "react";
import {
    orderBarChartPoints,
    rowsToTARSBarSeries,
    resolveLabelFillHex,
    type TARSBarChartPoint,
    type TARSBarChartTableData,
    type TARSChartConfiguration,
} from "./tarsBarChartHelpers";
import { resolveTarsPointFillHex } from "./tarsChartFillHelpers";
import { resolveTarsChartFontSizes, type TarsChartFontSizes } from "./tarsChartFontHelpers";
import { attachAm5ExportingToRoot, type Am5ChartExportApi } from "./tarsAm5Exporting";
import { prependRootTitleIfAny } from "./tarsAm5RootTitle";
import { bindAm5ExportRefs, clearAm5ExportRefs } from "./tars-chart-export-refs";
import TARSChartCopyImageToolbar from "./TARSChartCopyImageToolbar";

function buildDonutChartOnRoot(
    amChartsCore: any,
    amChartsPercent: any,
    chartRoot: any,
    sortedPoints: TARSBarChartPoint[],
    sliceLabelFill: string,
    fonts: TarsChartFontSizes
): void {
    const chart = chartRoot.container.children.push(
        amChartsPercent.PieChart.new(chartRoot, {
            innerRadius: amChartsCore.percent(48),
            radius: amChartsCore.percent(88),
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 8,
            paddingRight: 8,
        })
    );

    const pieSeries = chart.series.push(
        amChartsPercent.PieSeries.new(chartRoot, {
            name: "Series",
            valueField: "value",
            categoryField: "label",
            alignLabels: true,
        })
    );

    pieSeries.labels.template.setAll({
        visible: true,
        text: "[bold]{category}[/] - ({valuePercentTotal.formatNumber('#.#')}%)[/]",
        fontSize: fonts.dataLabel,
        fontWeight: "700",
        fill: amChartsCore.color(sliceLabelFill),
        oversizedBehavior: "wrap",
        lineHeight: 1.25,
        maxWidth: 240,
        populateText: true,
    });

    pieSeries.ticks.template.setAll({
        visible: true,
        stroke: amChartsCore.color("#64748b"),
        strokeWidth: 1,
        strokeOpacity: 0.9,
    });

    pieSeries.slices.template.setAll({
        stroke: amChartsCore.color("#ffffff"),
        strokeWidth: 2,
        strokeOpacity: 1,
        tooltipText: "{category}: [bold]{valuePercentTotal.formatNumber('#.#')}%[/] ({value})",
    });

    pieSeries.slices.template.adapters.add("fill", (_fill: unknown, target: any) => {
        const ctx = target.dataItem?.dataContext as TARSBarChartPoint | undefined;
        const idx = pieSeries.dataItems.indexOf(target.dataItem);
        return amChartsCore.color(resolveTarsPointFillHex(ctx, idx));
    });

    pieSeries.data.setAll(sortedPoints);
    pieSeries.appear(1000, 100);
    chart.appear(800, 100);
}

function mountAm5DonutInContainer(
    chartContainerElement: HTMLElement,
    points: TARSBarChartPoint[],
    chartTitle: string | null,
    configuration?: TARSChartConfiguration
): { dispose: () => void; exportApi: Am5ChartExportApi } | null {
    if (typeof window === "undefined" || points.length === 0) return null;

    const amChartsCore = (window as any).am5;
    const amChartsPercent = (window as any).am5percent;
    const animatedTheme = (window as any).am5themes_Animated;

    if (!amChartsCore?.Root?.new || !amChartsPercent?.PieChart?.new) {
        console.warn("TARSDonutChart: amCharts globals not ready");
        return null;
    }

    const chartRoot = amChartsCore.Root.new(chartContainerElement);
    if (animatedTheme) {
        chartRoot.setThemes([animatedTheme.new(chartRoot)]);
    }
    const fonts = resolveTarsChartFontSizes(configuration);
    prependRootTitleIfAny(amChartsCore, chartRoot, chartTitle, fonts.title);

    const sliceLabelFill = resolveLabelFillHex(configuration?.labelColors, "category", "#0f172a");

    try {
        buildDonutChartOnRoot(amChartsCore, amChartsPercent, chartRoot, points, sliceLabelFill, fonts);
    } catch (err) {
        console.error("TARSDonutChart: failed to build chart", err);
        chartRoot.dispose();
        return null;
    }

    requestAnimationFrame(() => {
        try {
            chartRoot.resize();
        } catch {
            /* optional */
        }
    });

    const exportApi = attachAm5ExportingToRoot(chartRoot);
    return { dispose: () => chartRoot.dispose(), exportApi };
}

export interface TARSDonutChartProps {
    data: TARSBarChartTableData;
    id?: string;
    chartCopyRef?: MutableRefObject<(() => Promise<void>) | null>;
    chartExportRef?: MutableRefObject<(() => Promise<Blob>) | null>;
}

export default function TARSDonutChart({
    data,
    id = "tars-donut-chart",
    chartCopyRef: chartCopyRefProp,
    chartExportRef,
}: TARSDonutChartProps) {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const disposeChartRef = useRef<(() => void) | null>(null);
    const internalChartCopyRef = useRef<(() => Promise<void>) | null>(null);
    const copyRef = chartCopyRefProp ?? internalChartCopyRef;

    const slicePoints = useMemo(() => {
        const raw = rowsToTARSBarSeries(data);
        return raw.filter((p) => p.value > 0);
    }, [data]);

    const sortedPoints = useMemo(
        () => orderBarChartPoints(slicePoints, data.preserveRowOrder),
        [slicePoints, data.preserveRowOrder]
    );

    const chartTitle = typeof data.title === "string" && data.title.trim() !== "" ? data.title.trim() : null;

    useLayoutEffect(() => {
        const el = chartContainerRef.current;
        disposeChartRef.current?.();
        disposeChartRef.current = null;
        clearAm5ExportRefs(copyRef, chartExportRef);
        if (!el || sortedPoints.length === 0) return undefined;

        const handle = mountAm5DonutInContainer(el, sortedPoints, chartTitle, data.configuration);
        disposeChartRef.current = handle ? () => handle.dispose() : null;
        bindAm5ExportRefs(handle?.exportApi, copyRef, chartExportRef);

        return () => {
            disposeChartRef.current?.();
            disposeChartRef.current = null;
            clearAm5ExportRefs(copyRef, chartExportRef);
        };
    }, [sortedPoints, chartTitle, data.configuration]);

    if (sortedPoints.length === 0) {
        return (
            <div className="flex flex-1 items-center justify-center px-8 py-16 text-[13px] font-medium text-slate-400">
                No positive numeric rows to display. Paste JSON with headers and rows.
            </div>
        );
    }

    return (
        <div className="flex min-h-0 w-full flex-1 flex-col">
            {!chartCopyRefProp ? (
                <div className="mb-2 flex shrink-0 justify-end">
                    <TARSChartCopyImageToolbar copyRef={internalChartCopyRef} />
                </div>
            ) : null}
            <div
                ref={chartContainerRef}
                id={id}
                className="min-h-0 min-h-[280px] w-full flex-1 basis-0"
                style={{ minHeight: "min(480px, 50vh)" }}
            />
        </div>
    );
}
